(() => {
  const selector = '[data-search-typewriter]';
  const instances = new WeakMap();

  class SearchTypewriter {
    constructor(input) {
      this.input = input;
      this.prefix = (input.dataset.prefix || '').trim();
      this.keywords = (input.dataset.keywords || '')
        .split(/\r?\n/)
        .map((keyword) => keyword.trim())
        .filter(Boolean);
      this.typeSpeed = this.toDelay(input.dataset.typeSpeed, 90);
      this.deleteSpeed = this.toDelay(input.dataset.deleteSpeed, 50);
      this.holdTime = this.toDelay(input.dataset.holdTime, 1200);
      this.keywordPause = this.toDelay(input.dataset.keywordPause, 400);
      this.showCaret = input.dataset.showCaret === 'true';
      this.finalPlaceholder = input.dataset.finalPlaceholder || '';
      this.finalAction = input.dataset.finalAction === 'stop' ? 'stop' : 'loop';
      this.fallback = input.dataset.staticPlaceholder || input.placeholder || '';
      this.enabled = input.dataset.enabled === 'true' && this.keywords.length > 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.runId = 0;
      this.delayTimer = null;
      this.resolveDelay = null;
      this.caretTimer = null;
      this.currentText = '';
      this.caretVisible = true;
      this.caretAllowed = false;

      this.onFocus = () => this.stop(true);
      this.onInput = () => this.stop(true);
      this.onBlur = () => {
        if (!this.input.value) this.start();
      };
      this.onMotionChange = () => {
        if (this.reducedMotion.matches) {
          this.stop(true);
        } else {
          this.start();
        }
      };

      input.addEventListener('focus', this.onFocus);
      input.addEventListener('input', this.onInput);
      input.addEventListener('blur', this.onBlur);
      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.onMotionChange);
      } else {
        this.reducedMotion.addListener(this.onMotionChange);
      }

      this.start();
    }

    toDelay(value, fallback) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
    }

    canAnimate(runId) {
      return (
        runId === this.runId &&
        this.input.isConnected &&
        this.enabled &&
        !this.reducedMotion.matches &&
        document.activeElement !== this.input &&
        !this.input.value
      );
    }

    render() {
      const caret = this.caretAllowed && this.showCaret && this.caretVisible ? '|' : '';
      this.input.placeholder = `${this.currentText}${caret}`;
    }

    startCaret() {
      this.stopCaret();
      if (!this.showCaret) return;

      this.caretTimer = window.setInterval(() => {
        this.caretVisible = !this.caretVisible;
        this.render();
      }, 500);
    }

    stopCaret() {
      if (this.caretTimer !== null) {
        window.clearInterval(this.caretTimer);
        this.caretTimer = null;
      }
    }

    cancelDelay() {
      if (this.delayTimer !== null) {
        window.clearTimeout(this.delayTimer);
        this.delayTimer = null;
      }
      if (this.resolveDelay) {
        const resolve = this.resolveDelay;
        this.resolveDelay = null;
        resolve();
      }
    }

    wait(duration) {
      return new Promise((resolve) => {
        this.resolveDelay = resolve;
        this.delayTimer = window.setTimeout(() => {
          this.delayTimer = null;
          this.resolveDelay = null;
          resolve();
        }, duration);
      });
    }

    stop(showFallback) {
      this.runId += 1;
      this.cancelDelay();
      this.stopCaret();
      this.caretAllowed = false;
      this.currentText = showFallback ? this.fallback : '';
      this.render();
    }

    start() {
      this.stop(true);

      if (
        !this.enabled ||
        this.reducedMotion.matches ||
        document.activeElement === this.input ||
        this.input.value
      ) {
        return;
      }

      const runId = this.runId;
      this.currentText = '';
      this.caretVisible = true;
      this.caretAllowed = true;
      this.render();
      this.startCaret();
      this.animate(runId);
    }

    async animate(runId) {
      while (this.canAnimate(runId)) {
        for (let index = 0; index < this.keywords.length; index += 1) {
          if (!this.canAnimate(runId)) return;

          const phraseText = [this.prefix, this.keywords[index]].filter(Boolean).join(' ');
          const phrase = Array.from(phraseText);
          const typedCharacters = [];

          for (const character of phrase) {
            if (!this.canAnimate(runId)) return;
            typedCharacters.push(character);
            this.currentText = typedCharacters.join('');
            this.caretVisible = true;
            this.render();
            await this.wait(this.typeSpeed);
          }

          if (!this.canAnimate(runId)) return;
          await this.wait(this.holdTime);

          while (typedCharacters.length > 0) {
            if (!this.canAnimate(runId)) return;
            typedCharacters.pop();
            this.currentText = typedCharacters.join('');
            this.caretVisible = true;
            this.render();
            await this.wait(this.deleteSpeed);
          }

          if (!this.canAnimate(runId)) return;
          await this.wait(this.keywordPause);
        }

        if (this.finalPlaceholder) {
          if (!this.canAnimate(runId)) return;
          this.currentText = this.finalPlaceholder;
          this.caretAllowed = false;
          this.render();

          if (this.finalAction === 'stop') {
            this.stopCaret();
            return;
          }

          await this.wait(this.holdTime);
          if (!this.canAnimate(runId)) return;
          this.currentText = '';
          this.caretVisible = true;
          this.caretAllowed = true;
          this.render();
          await this.wait(this.keywordPause);
        }
      }
    }

    destroy() {
      this.stop(true);
      this.input.removeEventListener('focus', this.onFocus);
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('blur', this.onBlur);
      if (this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener('change', this.onMotionChange);
      } else {
        this.reducedMotion.removeListener(this.onMotionChange);
      }
      instances.delete(this.input);
    }
  }

  const initialize = (root = document) => {
    root.querySelectorAll(selector).forEach((input) => {
      if (!instances.has(input)) instances.set(input, new SearchTypewriter(input));
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(selector).forEach((input) => instances.get(input)?.destroy());
  });
})();
